import React from 'react';
import {
  Box, Container, Grid, Typography, Divider, Chip,
  Avatar, Button, Paper,
} from '@mui/material';
import { ArrowForward, CalendarMonth, Person } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { departments } from '../data/hospitalData';
import { doctors } from '../data/hospitalData';
import { departmentDetails } from '../data/departmentDetails';

// ─── Photo Hero ───────────────────────────────────────────────────────────────

const DeptHero: React.FC<{ title: string; color: string }> = ({ title, color }) => (
  <Box
    sx={{
      position: 'relative',
      height: { xs: 200, md: 280 },
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(160deg, ${color}CC 0%, ${color}99 100%)`,
    }}
  >
    {/* Hospital silhouette pattern */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: 0.08,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='80'%3E%3Crect x='10' y='30' width='40' height='50' fill='white'/%3E%3Crect x='15' y='20' width='30' height='12' fill='white'/%3E%3Crect x='27' y='10' width='6' height='12' fill='white'/%3E%3Crect x='55' y='40' width='55' height='40' fill='white'/%3E%3Crect x='65' y='28' width='35' height='14' fill='white'/%3E%3Crect x='78' y='18' width='9' height='12' fill='white'/%3E%3Crect x='15' y='42' width='8' height='10' fill='%230a1628'/%3E%3Crect x='27' y='42' width='8' height='10' fill='%230a1628'/%3E%3Crect x='62' y='50' width='10' height='12' fill='%230a1628'/%3E%3Crect x='78' y='50' width='10' height='12' fill='%230a1628'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 140px',
      }}
    />
    {/* Soft white highlight */}
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', background: 'radial-gradient(ellipse at top right, rgba(255,255,255,0.12) 0%, transparent 65%)', pointerEvents: 'none' }} />
    <Typography
      variant="h2"
      sx={{
        position: 'relative',
        color: 'white',
        fontWeight: 700,
        textAlign: 'center',
        fontSize: { xs: '1.8rem', md: '3rem' },
        letterSpacing: '-0.5px',
        textShadow: '0 2px 12px rgba(0,0,0,0.5)',
        px: 2,
      }}
    >
      {title}
    </Typography>
  </Box>
);

// ─── Sub-category Card ────────────────────────────────────────────────────────

const SubCatCard: React.FC<{
  icon: string;
  title: string;
  bgColor: string;
  learnMore: string;
  onClick: () => void;
}> = ({ icon, title, bgColor, learnMore, onClick }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 0,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 4 },
      height: '100%',
    }}
  >
    {/* Photo placeholder */}
    <Box
      sx={{
        height: 160,
        background: `linear-gradient(135deg, ${bgColor}CC 0%, ${bgColor}88 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '3.5rem',
      }}
    >
      {icon}
    </Box>
    {/* Content */}
    <Box sx={{ p: 2 }}>
      <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3} sx={{ color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>
      <Typography
        onClick={onClick}
        sx={{
          color: 'primary.main',
          fontSize: '0.82rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {learnMore} <ArrowForward sx={{ fontSize: 14 }} />
      </Typography>
    </Box>
  </Box>
);

// ─── Doctor Sidebar ───────────────────────────────────────────────────────────

const DoctorSidebar: React.FC<{
  doctorId: string;
  titleEn: string;
  titleFr: string;
  specialtiesEn: string[];
  specialtiesFr: string[];
  language: string;
}> = ({ doctorId, titleEn, titleFr, specialtiesEn, specialtiesFr, language }) => {
  const navigate = useNavigate();
  const doctor = doctors.find((d) => d.id === doctorId);
  if (!doctor) return null;

  const title = language === 'en' ? titleEn : titleFr;
  const specialties = language === 'en' ? specialtiesEn : specialtiesFr;

  return (
    <Paper
      elevation={2}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        overflow: 'hidden',
        position: { md: 'sticky' },
        top: { md: 90 },
      }}
    >
      {/* Doctor photo */}
      <Box
        sx={{
          height: 220,
          background: `linear-gradient(145deg, ${doctor.color}22 0%, ${doctor.color}44 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          sx={{
            width: 130,
            height: 130,
            bgcolor: doctor.color,
            fontSize: '2.5rem',
            fontWeight: 800,
            border: '4px solid white',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
          }}
        >
          {doctor.initials}
        </Avatar>
      </Box>

      {/* Info */}
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={600} mb={0.25}>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight={800} color="text.primary" mb={0.5} lineHeight={1.3}>
          {doctor.name}
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* Specialties */}
        <Box sx={{ mb: 2.5 }}>
          {specialties.map((spec, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <Person sx={{ fontSize: 15, color: 'primary.main', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                {spec}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Links */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => navigate('/doctors')}
          >
            {language === 'en' ? 'Full Profile' : 'Profil Complet'}
          </Typography>
          <Typography
            sx={{
              color: 'primary.main',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => navigate('/doctors')}
          >
            {language === 'en' ? 'All Department Physicians' : 'Tous les Médecins du Service'}
          </Typography>
          <Button
            variant="contained"
            size="small"
            fullWidth
            startIcon={<CalendarMonth />}
            onClick={() => navigate('/appointment')}
            sx={{ mt: 1, textTransform: 'none', borderRadius: 0, fontWeight: 700 }}
          >
            {language === 'en' ? 'Book Appointment' : 'Prendre Rendez-vous'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const DepartmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const dept = departments.find((d) => d.id === id);
  const detail = id ? departmentDetails[id] : undefined;

  if (!dept || !detail) {
    return (
      <Box sx={{ py: 12, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">
          {language === 'en' ? 'Department not found.' : 'Service introuvable.'}
        </Typography>
        <Button onClick={() => navigate('/departments')} sx={{ mt: 3 }} variant="outlined">
          {language === 'en' ? 'View All Departments' : 'Tous les Services'}
        </Button>
      </Box>
    );
  }

  const deptName = language === 'en' ? dept.nameEn : dept.nameFr;
  const intro = language === 'en' ? detail.introEn : detail.introFr;
  const specialtyTitle = language === 'en' ? detail.specialtyTitleEn : detail.specialtyTitleFr;
  const focusSectionTitle = language === 'en' ? detail.focusSectionTitleEn : detail.focusSectionTitleFr;
  const focusAreas = language === 'en' ? detail.focusAreasEn : detail.focusAreasFr;
  const closingText = language === 'en' ? detail.closingTextEn : detail.closingTextFr;
  const additionalText = language === 'en' ? detail.additionalTextEn : detail.additionalTextFr;
  const learnMore = language === 'en' ? 'Learn more' : 'En savoir plus';

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* ── Hero ── */}
      <DeptHero title={deptName} color={dept.color} />

      {/* ── Sub-category cards ── */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {detail.subCategories.map((cat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <SubCatCard
                icon={cat.icon}
                title={language === 'en' ? cat.titleEn : cat.titleFr}
                bgColor={cat.bgColor}
                learnMore={learnMore}
                onClick={() => navigate('/appointment')}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Main content + Doctor sidebar ── */}
      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Grid container spacing={4}>

          {/* Left — clinical content */}
          <Grid item xs={12} md={8}>

            {/* Specialty title */}
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 800,
                fontSize: { xs: '1rem', md: '1.25rem' },
                letterSpacing: '0.04em',
                mb: 2,
              }}
            >
              {specialtyTitle}
            </Typography>

            {/* Intro */}
            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={1.5}>
              {intro}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Focus section title */}
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 800,
                fontSize: { xs: '1rem', md: '1.25rem' },
                letterSpacing: '0.04em',
                mb: 3,
              }}
            >
              {focusSectionTitle}
            </Typography>

            {/* Focus areas */}
            {focusAreas.map((area, i) => (
              <Box key={i} sx={{ mb: 3.5 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: 'primary.main', mb: 0.75, lineHeight: 1.4 }}
                >
                  {area.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.85} mb={1.25}>
                  {area.description}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {area.items.map((item, j) => (
                    <Typography
                      key={j}
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      lineHeight={1.85}
                      sx={{ mb: 0.5 }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}

            <Divider sx={{ my: 3 }} />

            {/* Closing text */}
            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={2}>
              {closingText}
            </Typography>

            {/* Additional text */}
            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>
              {additionalText}
            </Typography>

            {/* Dept tag */}
            <Chip
              label={language === 'en' ? detail.heroTaglineEn : detail.heroTaglineFr}
              sx={{
                bgcolor: `${dept.color}15`,
                color: dept.color,
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 1,
              }}
            />

            {/* CTA */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CalendarMonth />}
                onClick={() => navigate('/appointment')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 0 }}
              >
                {language === 'en' ? 'Book an Appointment' : 'Prendre un Rendez-vous'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/departments')}
                sx={{ textTransform: 'none', borderRadius: 0 }}
              >
                {language === 'en' ? 'All Departments' : 'Tous les Services'}
              </Button>
            </Box>
          </Grid>

          {/* Right — Doctor sidebar */}
          <Grid item xs={12} md={4}>
            <DoctorSidebar
              doctorId={detail.headDoctorId}
              titleEn={detail.doctorTitleEn}
              titleFr={detail.doctorTitleFr}
              specialtiesEn={detail.doctorSpecialtiesEn}
              specialtiesFr={detail.doctorSpecialtiesFr}
              language={language}
            />
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default DepartmentDetailPage;
